// 嵌套子表格展示关联结果
import { useCallback, useMemo } from 'react';
import { Progress, Table, TableColumnsType } from 'antd';
import Icon, { Icons } from 'icons';

import ItemIcon from 'components/common/item-icon/ItemIcon';
import BaseOverflowTooltip from 'components/common/overflow-tooltip/BaseOverflowTooltip';
import { classnames } from 'lib/classnames';
import useI18n from 'lib/hooks/useI18n';
import { Item, ItemType } from 'lib/types/models';

import legacyStyles from '../../index.module.less';
import styles from './index.module.less';

const cx = classnames(styles);
const legacyCx = classnames(legacyStyles);

interface RelationResultParams {
  items: Item[];
  results: any[];
  operateResults: any[];
}

const RelationResult: React.VFC<RelationResultParams> = ({ items, results, operateResults }) => {
  const { t } = useI18n();

  // 当前进度
  const percent = useMemo(() => {
    return Math.trunc(((operateResults?.length || 0) / (items?.length || 0)) * 100);
  }, [items?.length, operateResults?.length]);

  // 是否结束
  const isFinish = useMemo(() => {
    return (operateResults?.length && operateResults?.length === items?.length) || '';
  }, [items?.length, operateResults?.length]);

  const operateTip = useMemo(() => {
    if (isFinish) {
      return t('pages.itemsBatchOperate.default.operateComplete', { value: items?.length });
    } else {
      return t('pages.itemsBatchOperate.default.operateProgress', {
        current: (operateResults?.length || 0) + 1,
        num: items?.length,
      });
    }
  }, [isFinish, t, items?.length, operateResults?.length]);

  const itemRow = useCallback(
    row => (
      <div className={cx('option-box')}>
        <ItemIcon icon={(row.itemType as ItemType)?.icon} className={cx('item-icon')} />
        <BaseOverflowTooltip className={cx('item-overflow-tooltip')} title={row.key + ' ' + row.name}>
          <span className={cx('item-key')}>{row.key}</span>
          <span className={cx('item-name')}>{row.name}</span>
        </BaseOverflowTooltip>
      </div>
    ),
    [],
  );

  const expandedRowRender = record => {
    const columns: TableColumnsType = [
      {
        title: t('pages.itemsBatchOperate.columns.Relation.association'),
        dataIndex: 'linkType',
        key: 'linkType',
        render: linkType => <span className={cx('operate-detail-column-name')}>{linkType.name}</span>,
      },
      {
        title: t('pages.itemsBatchOperate.columns.Relation.relationItem'),
        dataIndex: 'destination',
        key: 'destination',
        render: destination => itemRow(destination),
      },
      {
        title: t('pages.itemsBatchOperate.columns.result'),
        dataIndex: 'error',
        key: 'error',
        width: 120,
        render: error => (
          <div className={legacyCx('batch-result-column')}>
            <Icon component={error ? Icons.Alert_Error : Icons.Alert_Success} />
            <span className={legacyCx('batch-result-column-text', { success: !error, error: !!error })}>
              {error ? t('global.fail') : t('global.success')}
            </span>
          </div>
        ),
      },
      {
        title: t('pages.LinkModal.modal.resultPage.table.reason'),
        dataIndex: 'error',
        key: 'error2',
        render: error => <span className={cx('operate-detail-column-name')}>{error || ''}</span>,
      },
    ];

    const data = results.filter(item => item.source?.objectId === record.objectId);

    return <Table className={cx('relation-table')} columns={columns} dataSource={data} pagination={false} />;
  };

  const columns = useMemo(() => {
    return [
      {
        title: t('pages.item.default.item'),
        width: '40%',
        dataIndex: 'name',
        key: 'name',
        ellipsis: true,
        render: (_, row) => <div className={cx('td')}>{itemRow(row)}</div>,
      },
      {
        title: t('pages.itemsBatchOperate.columns.result'),
        dataIndex: 'result',
        key: 'result',
        ellipsis: true,
        render: (_, row) => {
          const result = results.filter(item => item.source?.objectId === row.objectId);
          const successNum = result.filter(item => !item.error)?.length || 0;
          return (
            <div className={cx('relation-result-column')}>
              {t('pages.itemsBatchOperate.columns.Relation.successAndError', {
                successNum,
                errorNum: result.length - successNum,
              })}
            </div>
          );
        },
      },
    ];
  }, [itemRow, results, t]);

  return (
    <>
      <Progress percent={percent} size={['100%', 12]} />
      <p className={cx('batch-operate-confirm-header-tip')}>{operateTip}</p>
      {isFinish && (
        <div className={cx('batch-relation-result-wrap')}>
          <Table
            className={cx('unify-table-wrapper', 'operate-detail-table-wrap')}
            rowClassName={cx('unify-table-item')}
            columns={columns}
            rowKey="objectId"
            expandable={{ expandedRowRender }}
            dataSource={items}
          />
        </div>
      )}
    </>
  );
};

export default RelationResult;
